import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Clock, ChevronDown, ChevronRight, Users, Calendar, Info, Archive } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import api from '../utils/api';

const MONTH_LABELS_DK = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'];

export default function KonsulentTimer() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [showPeriodPicker, setShowPeriodPicker] = useState(false);

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-12

  // Default: viser de seneste 6 måneder
  const [fromYear, setFromYear] = useState(() => {
    const d = new Date(currentYear, currentMonth - 6, 1);
    return d.getFullYear();
  });
  const [fromMonth, setFromMonth] = useState(() => {
    const d = new Date(currentYear, currentMonth - 6, 1);
    return d.getMonth() + 1;
  });
  const [toYear, setToYear] = useState(currentYear);
  const [toMonth, setToMonth] = useState(currentMonth);

  const monthOptions = useMemo(() => MONTH_LABELS_DK.map((label, i) => ({ value: i + 1, label })), []);
  const yearOptions = useMemo(() => [currentYear - 1, currentYear, currentYear + 1], [currentYear]);

  const periodLabel = `${MONTH_LABELS_DK[fromMonth - 1]} ${fromYear} – ${MONTH_LABELS_DK[toMonth - 1]} ${toYear}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start_year: fromYear,
        start_month: fromMonth,
        end_year: toYear,
        end_month: toMonth,
      });
      const res = await api.get(`/consultant-hours/monthly?${params}`);
      setData(res.data);
      if (res.data?.months?.length > 0) {
        setExpandedMonth(res.data.months[res.data.months.length - 1].month_key);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  }, [fromYear, fromMonth, toYear, toMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleMonth = (key) => setExpandedMonth(expandedMonth === key ? null : key);

  // Beregnede totalsummeringer
  const periodTotal = useMemo(() => {
    if (!data?.months) return { business: 0, vacation: 0, sickness: 0, barsel: 0, forecast: 0, months: 0, employees: 0 };
    const t = { business: 0, vacation: 0, sickness: 0, barsel: 0, forecast: 0, months: data.months.length, employees: 0 };
    const empSet = new Set();
    for (const m of data.months) {
      t.business += m.total_business_hours || 0;
      t.vacation += m.total_vacation_hours || 0;
      t.sickness += m.total_sickness_hours || 0;
      t.barsel   += m.total_barsel_hours   || 0;
      t.forecast += m.total_forecasted_vacation || 0;
      (m.employees || []).forEach(e => empSet.add(e.email || e.name));
    }
    t.employees = empSet.size;
    return t;
  }, [data]);

  const [showVacationDetail, setShowVacationDetail] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const formatNum = (n) => Number(n || 0).toLocaleString('da-DK', { maximumFractionDigits: 1 });

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="consultant-hours-page">
      <header className="bg-[#1A1A1A] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Navigation />
            <h1 className="text-xl font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#1E6F7A]" />
              Konsulenttimer
              <button
                onClick={() => setShowInfoModal(true)}
                className="text-white/40 hover:text-[#1E6F7A] transition-colors ml-1"
                title="Sådan beregnes konsulenttimer"
                data-testid="konsulenttimer-info-btn"
              >
                <Info className="w-4 h-4" />
              </button>
            </h1>
          </div>

          {/* Period Picker */}
          <div className="relative">
            <button
              onClick={() => setShowPeriodPicker(!showPeriodPicker)}
              className="bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white flex items-center gap-2 hover:bg-white/5 transition-colors"
              data-testid="period-picker-btn"
            >
              <Calendar className="w-4 h-4 text-[#1E6F7A]" />
              {periodLabel}
              <ChevronDown className="w-4 h-4 text-white/40" />
            </button>
            {showPeriodPicker && (
              <div className="absolute right-0 top-full mt-1 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-xl z-50 p-4 w-80">
                {/* Fra */}
                <div className="mb-3">
                  <label className="text-xs text-gray-400 mb-1 block">Fra</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={fromMonth}
                      onChange={(e) => setFromMonth(parseInt(e.target.value))}
                      className="bg-[#0B0C10] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#1E6F7A] focus:outline-none"
                      data-testid="from-month-select"
                    >
                      {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <select
                      value={fromYear}
                      onChange={(e) => setFromYear(parseInt(e.target.value))}
                      className="bg-[#0B0C10] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#1E6F7A] focus:outline-none"
                      data-testid="from-year-select"
                    >
                      {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>

                {/* Til */}
                <div className="mb-3">
                  <label className="text-xs text-gray-400 mb-1 block">Til</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={toMonth}
                      onChange={(e) => setToMonth(parseInt(e.target.value))}
                      className="bg-[#0B0C10] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#1E6F7A] focus:outline-none"
                      data-testid="to-month-select"
                    >
                      {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <select
                      value={toYear}
                      onChange={(e) => setToYear(parseInt(e.target.value))}
                      className="bg-[#0B0C10] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#1E6F7A] focus:outline-none"
                      data-testid="to-year-select"
                    >
                      {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>

                {/* Hurtigvalg */}
                <div className="border-t border-white/10 pt-3 space-y-1">
                  <p className="text-xs text-gray-500 mb-1">Hurtigvalg</p>
                  {[
                    { label: 'Hele året (igangværende)', fm: 1, fy: currentYear, tm: 12, ty: currentYear },
                    { label: 'Sidste 3 måneder', fm: ((currentMonth - 3 + 11) % 12) + 1, fy: currentMonth - 3 <= 0 ? currentYear - 1 : currentYear, tm: currentMonth, ty: currentYear },
                    { label: 'Sidste 6 måneder', fm: ((currentMonth - 6 + 11) % 12) + 1, fy: currentMonth - 6 <= 0 ? currentYear - 1 : currentYear, tm: currentMonth, ty: currentYear },
                    { label: 'Sidste 12 måneder', fm: ((currentMonth - 12 + 11) % 12) + 1, fy: currentMonth - 12 <= 0 ? currentYear - 1 : currentYear, tm: currentMonth, ty: currentYear },
                  ].map(p => (
                    <button
                      key={p.label}
                      onClick={() => { setFromMonth(p.fm); setFromYear(p.fy); setToMonth(p.tm); setToYear(p.ty); setShowPeriodPicker(false); }}
                      className="w-full text-left px-3 py-1.5 rounded-lg text-sm text-white/70 hover:bg-white/5 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setShowPeriodPicker(false)}
                  className="w-full mt-3 bg-[#1E6F7A] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#1E6F7A]/80 transition-colors"
                  data-testid="apply-period-btn"
                >
                  Anvend
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <p className="text-gray-400 text-sm mb-6">Månedlig fordeling af konsulenttimer — data fra Google Sheets-fanen &quot;Budget Timer&quot;</p>

        {/* Info Modal - Sådan beregnes konsulenttimer */}
        {showInfoModal && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowInfoModal(false)}
            data-testid="konsulenttimer-info-modal"
          >
            <div
              className="bg-[#1a1a1a] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-[#1E6F7A]" />
                  <h2 className="text-white font-semibold text-lg">Sådan beregnes konsulenttimer</h2>
                </div>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="text-gray-400 hover:text-white text-sm"
                  data-testid="close-info-modal"
                >
                  ✕
                </button>
              </div>
              <div className="overflow-y-auto px-6 py-5 space-y-5 text-sm text-white/80">

                <section>
                  <h3 className="text-white font-semibold mb-2">1. Kilde</h3>
                  <p>
                    Alle tal hentes fra Google Sheets-arket <em>Leadvalidator</em> → fanen{' '}
                    <span className="text-[#1E6F7A] font-medium">&quot;Budget Timer&quot;</span>. Én række pr.
                    kombination af <em>(medarbejder, måned)</em> med færdig-aggregerede timer.
                  </p>
                </section>

                <section>
                  <h3 className="text-white font-semibold mb-2">2. Hvem tæller med?</h3>
                  <ul className="list-disc list-inside space-y-1 text-white/70">
                    <li>Kun medarbejdere med type <strong>Konsulent</strong> eller <strong>Konsulent/Montør</strong> i fanen <em>Konsulent_Data</em> (kol. D).</li>
                    <li>Andre roller (fx ren Montør, admin) ekskluderes fra siden.</li>
                    <li>Medarbejdere efter deres slutdato (kol. I i <em>Konsulent_Data</em>) tælles ikke med i måneder efter slutdatoen.</li>
                    <li>Medarbejdere før deres startdato tæller ikke med.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-white font-semibold mb-2">3. Formel pr. medarbejder pr. måned</h3>
                  <div className="bg-[#0B0C10] rounded-lg p-3 border border-white/5 font-mono text-xs space-y-1">
                    <div className="text-white/70">Rå timer = brutto × multiplier</div>
                    <div className="text-[#1E6F7A]">Timer = max(0, brutto − ferie − sygdom − barsel − kursus − barns sygedag) × multiplier</div>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-white/70 mt-2">
                    <li><strong>brutto</strong> = kolonnen <code>business_hours_total</code> i Budget Timer</li>
                    <li><strong>ferie / sygdom / barsel / kursus / barns sygedag</strong> = <code>vacation_hours</code> / <code>sickness_hours</code> / <code>barsel_hours</code> / <code>kursus_hours</code> / <code>barns_sygedag_hours</code> for samme række. <strong>Reklamation trækkes IKKE fra.</strong></li>
                    <li><strong>multiplier</strong> = <span className="text-amber-300">0,6</span> hvis Konsulent/Montør (fordi ~40% går til montering), ellers <span className="text-white">1,0</span></li>
                    <li>Kolonnen <strong>Rå timer</strong> viser brutto × multiplier (uden fravær-fradrag). <strong>Timer</strong>-kolonnen er nettoværdien efter fravær.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-white font-semibold mb-2">4. Fremtidige måneder — forventet ferie</h3>
                  <p className="text-white/70 mb-2">
                    For måneder <em>efter</em> indeværende måned trækkes den forventede resterende ferie også fra:
                  </p>
                  <div className="bg-[#0B0C10] rounded-lg p-3 border border-white/5 font-mono text-xs text-amber-300 space-y-1">
                    <div>årlig_ret = ugentlige_timer × 5 × (ansat_måneder / 12)</div>
                    <div>rest_rå = max(0, årlig_ret − ferie_afholdt_YTD)</div>
                    <div>rest_vist = rest_rå × multiplier (0,6 for K/M)</div>
                    <div>forv_pr_mdr = rest_vist / antal_resterende_måneder</div>
                    <div className="text-white pt-1 border-t border-white/10">business_hours_net −= forv_pr_mdr</div>
                  </div>
                  <p className="text-white/60 text-xs mt-2">
                    Klik på det amber-farvede KPI-kort <em>&quot;Resterende ferie (i år)&quot;</em> for at se breakdown pr. medarbejder.
                  </p>
                </section>

                <section>
                  <h3 className="text-white font-semibold mb-2">5. Måneds- og periodesummer</h3>
                  <ul className="list-disc list-inside space-y-1 text-white/70">
                    <li><strong>Månedstotal</strong> = summen af <code>business_hours_net</code> for alle medarbejdere i den måned</li>
                    <li><strong>Total business-timer</strong> (øverst) = summen af alle måneders totaler i den valgte periode</li>
                    <li><strong>Total fravær</strong> = ferie + sygdom + barsel (rå timer, uden multiplier)</li>
                  </ul>
                </section>

                <section className="bg-[#0B0C10] rounded-lg p-3 border border-[#1E6F7A]/30">
                  <p className="text-xs text-white/60">
                    <strong className="text-[#1E6F7A]">Bemærk:</strong> Selve <em>Budget Timer</em>-fanen bygges automatisk i Google Sheets ud fra
                    kalender- og fraværsdata (<em>Data_klar_1</em>). Dashboarded aggregerer ikke minutter selv — det læser kun de færdige månedsrækker.
                  </p>
                </section>

                <section>
                  <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <Archive className="w-4 h-4 text-purple-300" />
                    6. Arkiv — bevarelse af historik
                  </h3>
                  <p className="text-white/70 mb-2">
                    Når en konsulent stopper, fjernes deres kalender ofte fra Budget Timer med tilbagevirkende kraft.
                    For at bevare historikken tager dashboarded automatisk et <strong>snapshot</strong> i MongoDB af hver række hver gang siden hentes.
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-white/70">
                    <li>Rækker der forsvinder fra Sheet&apos;et hentes automatisk fra arkiv</li>
                    <li>Live Sheet-data <strong>vinder altid</strong> — rettelser i Sheet&apos;et slår stadig igennem</li>
                    <li>Arkivrækker markeres med <span className="inline-flex items-center gap-1 bg-purple-500/20 border border-purple-400/30 text-purple-300 text-[10px] px-1.5 py-0.5 rounded-full"><Archive className="w-2.5 h-2.5" /></span> ikon</li>
                    <li>Historik bevares i <strong>op til 24 måneder</strong>, hvorefter ældste måneder automatisk slettes</li>
                  </ul>
                </section>

              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {data && !loading && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
              <p className="text-xs text-gray-400 mb-1">Total timer (netto)</p>
              <p className="text-xl font-bold text-white" data-testid="total-business">{formatNum(periodTotal.business)}</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
              <p className="text-xs text-gray-400 mb-1">Total fravær (ferie+syg+barsel)</p>
              <p className="text-xl font-bold text-white" data-testid="total-absence">{formatNum(periodTotal.vacation + periodTotal.sickness + periodTotal.barsel)}</p>
            </div>
            <div
              className="bg-[#1a1a1a] rounded-xl p-4 border border-amber-500/30 cursor-pointer hover:bg-amber-500/5 transition-colors"
              onClick={() => setShowVacationDetail(true)}
              data-testid="total-remaining-vacation-card"
              role="button"
              tabIndex={0}
            >
              <p className="text-xs text-amber-400/80 mb-1">Resterende ferie (i år)</p>
              <p className="text-xl font-bold text-amber-300" data-testid="total-remaining-vacation">
                {formatNum(data?.vacation_totals?.total_remaining_shown)}
              </p>
              <p className="text-[10px] text-gray-500 mt-1">Klik for fordeling pr. medarbejder</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
              <p className="text-xs text-gray-400 mb-1">Antal måneder</p>
              <p className="text-xl font-bold text-white" data-testid="total-months">{periodTotal.months}</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
              <p className="text-xs text-gray-400 mb-1">Unikke medarbejdere</p>
              <p className="text-xl font-bold text-white" data-testid="total-employees">{periodTotal.employees}</p>
            </div>
          </div>
        )}

        {/* Vacation Detail Modal */}
        {showVacationDetail && data?.vacation_summary && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowVacationDetail(false)}
            data-testid="vacation-detail-modal"
          >
            <div
              className="bg-[#1a1a1a] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h2 className="text-white font-semibold text-lg">Resterende ferie pr. medarbejder</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Indeværende år ({data.vacation_totals?.current_year}) · Pro-rata på ansættelsesperiode · Konsulent/Montør × 0,6
                  </p>
                </div>
                <button
                  onClick={() => setShowVacationDetail(false)}
                  className="text-gray-400 hover:text-white text-sm"
                  data-testid="close-vacation-modal"
                >
                  ✕
                </button>
              </div>
              <div className="overflow-x-auto overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#0B0C10] sticky top-0">
                    <tr className="text-xs text-gray-400 border-b border-white/5">
                      <th className="text-left px-4 py-2 font-medium">Medarbejder</th>
                      <th className="text-left px-4 py-2 font-medium">Rolle</th>
                      <th className="text-right px-4 py-2 font-medium">Årlig ret</th>
                      <th className="text-right px-4 py-2 font-medium">Afholdt YTD</th>
                      <th className="text-right px-4 py-2 font-medium">Rest (rå)</th>
                      <th className="text-right px-4 py-2 font-medium">Rest (vist)</th>
                      <th className="text-right px-4 py-2 font-medium">Pr. mdr</th>
                      <th className="text-right px-4 py-2 font-medium">Mdr tilbage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.vacation_summary.map(v => (
                      <tr key={v.email} className="border-b border-white/5 last:border-0 hover:bg-white/5">
                        <td className="px-4 py-2 text-white">{v.name}</td>
                        <td className="px-4 py-2 text-gray-400 text-xs">{v.role || '—'}</td>
                        <td className="px-4 py-2 text-right text-gray-300">{formatNum(v.annual_entitlement)}</td>
                        <td className="px-4 py-2 text-right text-blue-400/80">{formatNum(v.vacation_taken_ytd)}</td>
                        <td className="px-4 py-2 text-right text-gray-400">{formatNum(v.remaining_raw)}</td>
                        <td className="px-4 py-2 text-right text-amber-300 font-semibold">{formatNum(v.remaining_shown)}</td>
                        <td className="px-4 py-2 text-right text-amber-200/80">{formatNum(v.forecasted_monthly)}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{v.remaining_months}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#0B0C10]">
                    <tr className="text-sm font-semibold border-t border-white/10">
                      <td className="px-4 py-2 text-white">Total</td>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2 text-right text-blue-400">{formatNum(data.vacation_totals?.total_taken_ytd)}</td>
                      <td className="px-4 py-2 text-right text-gray-300">{formatNum(data.vacation_totals?.total_remaining_raw)}</td>
                      <td className="px-4 py-2 text-right text-amber-300">{formatNum(data.vacation_totals?.total_remaining_shown)}</td>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-center">
            {error}
          </div>
        )}

        {/* Month List */}
        {data && !loading && (
          <div className="space-y-2">
            {data.months?.map(month => (
              <div key={month.month_key} className="bg-[#1a1a1a] rounded-xl border border-white/5 overflow-hidden">
                {/* Month Header */}
                <button
                  onClick={() => toggleMonth(month.month_key)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  data-testid={`month-${month.month_key}`}
                >
                  <div className="flex items-center gap-4">
                    {expandedMonth === month.month_key ? (
                      <ChevronDown className="w-5 h-5 text-[#1E6F7A]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-white/40" />
                    )}
                    <div className="text-left">
                      <div className="text-white font-semibold flex items-center gap-2">
                        {month.label}
                        {month.snapshot_count > 0 && (
                          <span
                            className="inline-flex items-center gap-1 bg-purple-500/15 border border-purple-400/30 text-purple-300 text-[10px] px-2 py-0.5 rounded-full"
                            title={`${month.snapshot_count} række(r) hentet fra arkiv (konsulent findes ikke længere i kalenderen)`}
                            data-testid={`snapshot-badge-${month.month_key}`}
                          >
                            <Archive className="w-3 h-3" />
                            {month.snapshot_count} fra arkiv
                          </span>
                        )}
                      </div>
                      <div className="text-gray-500 text-xs flex items-center gap-1">
                        <Users className="w-3 h-3" /> {month.employee_count} medarbejdere
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-right">
                    <div>
                      <div className="text-white font-bold">{formatNum(month.total_business_hours)} timer</div>
                      <div className="text-gray-500 text-xs">
                        Ferie: {formatNum(month.total_vacation_hours)} · Syg: {formatNum(month.total_sickness_hours)} · Barsel: {formatNum(month.total_barsel_hours)}
                        {month.is_future && month.total_forecasted_vacation > 0 && (
                          <span className="ml-2 text-amber-400/80">· Forv. ferie: {formatNum(month.total_forecasted_vacation)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Expanded Month Detail */}
                {expandedMonth === month.month_key && (
                  <div className="border-t border-white/5 overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-xs text-gray-500 border-b border-white/5">
                          <th className="text-left px-4 py-2 font-medium">Medarbejder</th>
                          <th className="text-left px-4 py-2 font-medium hidden md:table-cell">Region</th>
                          <th className="text-left px-4 py-2 font-medium hidden md:table-cell">Rolle</th>
                          <th className="text-right px-4 py-2 font-medium" title="Brutto-timer fra Budget Timer × multiplier (0,6 for Konsulent/Montør). Fravær er IKKE trukket fra.">Rå timer</th>
                          <th className="text-right px-4 py-2 font-medium" title="Rå timer minus ferie, sygdom og barsel (netto)">Timer</th>
                          <th className="text-right px-4 py-2 font-medium hidden lg:table-cell">Ferie</th>
                          <th className="text-right px-4 py-2 font-medium hidden lg:table-cell">Syg</th>
                          <th className="text-right px-4 py-2 font-medium hidden lg:table-cell">Barsel</th>
                          {month.is_future && (
                            <th className="text-right px-4 py-2 font-medium text-amber-400/80" title="Forventet ferie baseret på resterende årlig ret fordelt på resterende måneder">Forv. ferie</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {month.employees?.map(c => (
                          <tr
                            key={c.email || c.name}
                            className={`border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors ${c.is_from_snapshot ? 'bg-purple-500/5' : ''}`}
                            data-testid={c.is_from_snapshot ? `snapshot-row-${c.email}` : undefined}
                          >
                            <td className="px-4 py-2.5">
                              <span className="text-white text-sm flex items-center gap-2">
                                {c.name}
                                {c.is_from_snapshot && (
                                  <span
                                    className="inline-flex items-center bg-purple-500/20 border border-purple-400/30 text-purple-300 text-[10px] px-1.5 py-0.5 rounded-full"
                                    title="Fra arkiv — konsulent findes ikke længere i kalenderen"
                                  >
                                    <Archive className="w-2.5 h-2.5" />
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 hidden md:table-cell"><span className="text-gray-400 text-xs">{c.region || '—'}</span></td>
                            <td className="px-4 py-2.5 hidden md:table-cell"><span className="text-gray-400 text-xs">{c.role || '—'}</span></td>
                            <td className="px-4 py-2.5 text-right text-sm text-white/70">{formatNum((c.business_hours_raw || 0) * (c.type_multiplier || 1))}</td>
                            <td className="px-4 py-2.5 text-right text-sm font-medium text-[#1E6F7A]">{formatNum(c.business_hours)}</td>
                            <td className="px-4 py-2.5 text-right text-sm text-blue-400/80 hidden lg:table-cell">{c.vacation_hours > 0 ? formatNum(c.vacation_hours) : '—'}</td>
                            <td className="px-4 py-2.5 text-right text-sm text-red-400/80 hidden lg:table-cell">{c.sickness_hours > 0 ? formatNum(c.sickness_hours) : '—'}</td>
                            <td className="px-4 py-2.5 text-right text-sm text-pink-400/80 hidden lg:table-cell">{c.barsel_hours > 0 ? formatNum(c.barsel_hours) : '—'}</td>
                            {month.is_future && (
                              <td className="px-4 py-2.5 text-right text-sm text-amber-300/90">
                                {c.forecasted_vacation_hours > 0 ? formatNum(c.forecasted_vacation_hours) : '—'}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      {month.is_future && month.total_forecasted_vacation > 0 && (
                        <tfoot>
                          <tr className="border-t border-white/10 text-xs">
                            <td className="px-4 py-2 text-gray-400 font-semibold">Total</td>
                            <td className="px-4 py-2 hidden md:table-cell"></td>
                            <td className="px-4 py-2 hidden md:table-cell"></td>
                            <td className="px-4 py-2 text-right text-white/70 font-semibold">
                              {formatNum((month.employees || []).reduce((s, e) => s + ((e.business_hours_raw || 0) * (e.type_multiplier || 1)), 0))}
                            </td>
                            <td className="px-4 py-2 text-right text-[#1E6F7A] font-semibold">{formatNum(month.total_business_hours)}</td>
                            <td className="px-4 py-2 text-right text-blue-400/80 hidden lg:table-cell">{formatNum(month.total_vacation_hours)}</td>
                            <td className="px-4 py-2 text-right text-red-400/80 hidden lg:table-cell">{formatNum(month.total_sickness_hours)}</td>
                            <td className="px-4 py-2 text-right text-pink-400/80 hidden lg:table-cell">{formatNum(month.total_barsel_hours)}</td>
                            <td className="px-4 py-2 text-right text-amber-300 font-semibold">{formatNum(month.total_forecasted_vacation)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {data && !loading && data.months?.length === 0 && (
          <div className="text-center py-20">
            <Clock className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-gray-400">Ingen data tilgængelig for den valgte periode</p>
          </div>
        )}
      </div>
    </div>
  );
}
